import { Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { ImagesContainer } from 'ZalamoLibrary/src/lib/upload/graphql';
import { Required } from 'ZalamoLibrary/src/lib/core/decorators/Required';
import { PhotoGroupList } from 'ZalamoLibrary/src/lib/ui/products/shopping-cart-back/actions/standalone';
import { ZalamoDialogService } from '../../../zalamo-dialog/zalamo-dialog.service';
import { WallPieceCropComponent } from 'ZalamoLibrary/src/lib/ui/look-book-front/wall-decor-front/edit-wall-decor-piece/wall-piece-crop/wall-piece-crop.component';
import { getPhotoGroupFromPhotoId } from 'ZalamoLibrary/src/lib/ui/look-book-front/wall-decor-front/helpers';
import { first, takeUntil, tap } from 'rxjs/operators';
import { UntypedFormControl } from '@angular/forms';
import { SingleImageInWallDecorMap } from '@ui/look-book-front/wall-decor-front/types';
import { FullscreenService } from '@ui/fullscreen/fullscreen.service';
import { DirtyForm, DirtyFormInterface } from '@ui/form/validation/DirtyForm';
import { applyMixins } from 'ZalamoLibrary/src/lib/core/classes';
import { FaceDetectorService } from 'ZalamoLibrary/src/lib/zalamo/face-detection/face-detector.service';

@Component({
    selector: 'lib-edit-wall-decor-piece',
    templateUrl: './edit-wall-decor-piece.component.html',
    styleUrls: ['./edit-wall-decor-piece.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class EditWallDecorPieceComponent implements OnInit {
  @Input() context: ImagesContainer;
  @Input() contextId: ImagesContainer;
  @Input() availableImages: string[] = [];
  @Input() @Required current: string;
  @Input() @Required imageRelations: PhotoGroupList[] = [];
  @Input() @Required rounding = 0;
  @Output() picked = new EventEmitter<string>();
  @Output() cropped = new EventEmitter<{
    x: number;
    y: number;
    w: number;
    h: number;
  }>();
  @Input() ratio = 1;

  set previous({ crop, photoId }: { crop; photoId }) {
    if (crop) {
      this.currentCrop.setValue(crop);
    }
    if (photoId) {
      this.photoId = photoId;
    }
    this.currentFormValue = this.takeSnapshotForDirty();
  }

  currentCrop = new UntypedFormControl();
  saved = new EventEmitter<{
    photoId: string;
    isFlip: boolean;
    crop: {
      x: number;
      y: number;
      w: number;
      h: number;
    };
  }>();
  private _photoId;
  isFlip = false;
  otherImages: Record<string, SingleImageInWallDecorMap>;

  get photoId() {
    return this._photoId;
  }

  originalImage = null;

  set photoId(value) {
    this._photoId = value;
  }

  constructor(
    private zalamoDialogService: ZalamoDialogService,
    public fullscreenService: FullscreenService,
    private faceDetectorService: FaceDetectorService
  ) {}

  takeSnapshotForDirty(): string {
    return JSON.stringify({
      crop: this.currentCrop.value,
      photoId: this.photoId,
    });
  }

  ngOnInit(): void {
    this.currentFormValue = this.takeSnapshotForDirty();
  }

  photoPicked(photoId) {
    if (this.photoId === photoId) {
      this.startCrop(photoId);
      return;
    }
    this.photoId = photoId;
    const photoDetails = getPhotoGroupFromPhotoId(photoId, this.imageRelations);
    const imageRatio = photoDetails.width / photoDetails.height;

    this.faceDetectorService
      .detectFacesByPhotoId(photoId, this.ratio)
      .pipe(
        tap((faces) => {
          this.currentCrop.patchValue(faces.maxSquare);
        })
      )
      .pipe(first())
      .subscribe();
  }

  startCrop(photoId) {
    console.log(this.imageRelations);
    const ref = this.zalamoDialogService.openRight(WallPieceCropComponent);
    ref.componentInstance.availableImages = this.availableImages;
    ref.componentInstance.context = this.context;
    ref.componentInstance.contextId = this.contextId;
    ref.componentInstance.imageRelations = this.imageRelations;
    ref.componentInstance.photoId = photoId;
    ref.componentInstance.rounding = this.rounding;
    ref.componentInstance.currentCropValue = {
      x: -this.currentCrop.value.x / 100,
      y: -this.currentCrop.value.y / 100,
      w: this.currentCrop.value.w / 100,
      h: this.currentCrop.value.h / 100,
    };
    ref.componentInstance.containerRatio = this.ratio;
    const photoDetails = getPhotoGroupFromPhotoId(photoId, this.imageRelations);
    ref.componentInstance.imageRatio = photoDetails.width / photoDetails.height;
    ref.componentInstance.saved
      .pipe(
        takeUntil(ref.afterClosed()),
        tap((crop) => {
          this.currentCrop.patchValue(crop);
          this.cropped.emit({
            h: (crop.h / 100) * photoDetails.height,
            w: (crop.w / 100) * photoDetails.width,
            x: (crop.x / 100) * photoDetails.width,
            y: (crop.y / 100) * photoDetails.height,
          });
          ref.close();
        })
      )
      .subscribe();
  }

  save() {
    this.saved.next({
      photoId: this._photoId,
      crop: this.currentCrop.value,
      isFlip: this.isFlip,
    });
  }
}

export interface EditWallDecorPieceComponent extends DirtyFormInterface {}

applyMixins(EditWallDecorPieceComponent, [DirtyForm]);
